import _ from 'lodash';
import * as R from 'ramda';
import Vue from 'vue';
import checklistDB from '@mobile/db/checklists';
import todoDB from '@mobile/db/todos';
import inspectionQueueDB from '@mobile/db/inspections';
import draftDB from '@mobile/db/drafts';
import inspectionService from '@mobile/services/inspection';
import upgradeService from '@mobile/services/upgrade';
import config from 'config';
import { eventBus } from '@/eventBus';
import encoders from '@/store/encoders';
import { INSPECTION_ITEM_TYPE, INSPECTION_ITEM_RESPONSE } from '@/constants';
import { now } from '@/util/UnixTimestamps';

const validateInspectionItem = (item) => {
    const isRequired = item.required && !item.responded;
    const isNumber = item.response_type === INSPECTION_ITEM_RESPONSE.NUMBER && item.responded && Number.isNaN(Number(item.response));

    const errors = [];
    if (isRequired) errors.push(window.i18n.t('This question is required.'));
    if (isNumber) errors.push(window.i18n.t('Response must be a number. At most one decimal separator, and no thousand separator.'));

    return errors;
};

const isInvalidItem = ({ type, allConditionsMet, repeat }) => {
    const isConditionalWithoutResponse = type === INSPECTION_ITEM_TYPE.CONDITIONAL && !allConditionsMet;
    const isRepeatableSection = repeat;

    return isConditionalWithoutResponse || isRepeatableSection;
};

const validateInspection = (acc, item) => {
    if (isInvalidItem(item)) return acc;
    if (item.type !== INSPECTION_ITEM_TYPE.QUESTION) {
        return {
            ...acc,
            ...(item.items && item.items.reduce(validateInspection, {})),
        };
    }

    const errors = validateInspectionItem(item);
    if (errors.length <= 0) return acc;

    return {
        ...acc,
        [item.uuid]: errors,
    };
};

export default {
    namespaced: true,

    state: getDefaultState(),

    getters: {
        autoSaveEnabled: (state) => state.autoSaveEnabled,
        inspection: (state) => state.inspection,
        currentPage: (state) => state.currentPage,
        showErrors: (state) => state.showErrors,
        errors: (state) => state.errors,
        currentPageHasErrors: (state) => (Object.keys(state.errors).length > 0),
        find_response_set: (state) => (response_set_uuid) => {
            if (state.inspection.form) {
                return findResponseSet(state.inspection.form.params.response_sets, response_set_uuid);
            }
        },
    },

    actions: {
        inspectionSubmitted({ state, commit, dispatch }) {
            commit('disableAutoSave');
            return new Promise((resolve, reject) => {
                if (!state.inspection.form && !state.inspection.form.items) return reject(window.i18n.t('Invalid inspection format'));
                const errors = state.inspection.form.items.reduce(validateInspection, {});

                if (R.isEmpty(errors)) {
                    dispatch('save')
                        .then(() => dispatch('inspections/sendQueue', null, { root: true }))
                        .finally(resolve);
                } else {
                    commit('inspectionValidationFailed', errors);
                    commit('enableAutoSave');
                    reject(errors);
                }
            });
        },

        async fromTodo({ commit, rootGetters, dispatch }, todoId) {
            let todo = await todoDB.get(todoId);

            if (!todo) {
                return Promise.reject({ message: window.i18n.t('Todo not found!') });
            }

            if (todo.inspection.form.params.format_version !== config.jsonFormatVersion) {
                await todoDB.update(todo.id);
                todo = await todoDB.get(todoId);
            }

            if (todo.inspection.form.params.format_version !== config.jsonFormatVersion) {
                return Promise.reject({
                    message: window.i18n.t('Incompatible form version: {formVersion} instead of {systemVersion}', {
                        formVersion: todo.form.params.format_version,
                        systemVersion: config.jsonFormatVersion,
                    }),
                });
            }

            commit('start', {
                inspection_id: todo.inspection.inspection_id,
                user_id: rootGetters['authentication/user'].id,
                checklist: todo.inspection.checklist_id,
                todo: todo.id,
                checklist_title: todo.inspection.checklist_title,
                checklist_description: todo.inspection.checklist_description,
                schedule_title: todo.inspection.schedule_title,
                status: 0,
                site: todo.site,
                sites: todo.inspection.checklist_sites,
                users: todo.inspection.checklist_users,
                due_at: todo.due_at,
                started_at: null,
                conducted_at: null,
                form: todo.inspection.form,
            });

            const draftId = await dispatch('saveDraft');
            await dispatch('fromDraft', draftId);
        },

        async fromDraft({ commit }, draftId) {
            const draft = await draftDB.get(draftId);

            if (!draft) {
                return Promise.reject({ message: window.i18n.t('Draft not found!') });
            }

            // when resuming, we check if the draft is based on a todo. If it is, we check if the todo
            // is still there. If someone else already did it, then it might not be there.
            if (draft.todo) {
                let todo = await todoDB.get(draft.todo);
                if (!todo) {
                    return Promise.reject({ message: window.i18n.t('Can not resume, original todo is not found.') });
                }
                todo = null;
            }

            if (draft.form.params.format_version !== config.jsonFormatVersion) {
                const upgradedForm = await upgradeService.upgrade(draft.form);
                if (upgradedForm) {
                    draft.form = upgradedForm;
                    await draftDB.put(draft);
                } else {
                    return Promise.reject({
                        message: window.i18n.t('Error upgrading draft from version {original} to {upgraded}', {
                            original: draft.form.params.format_version,
                            upgraded: config.jsonFormatVersion,
                        }),
                    });
                }
            }

            if (draft) {
                draft.draft = draft.id;
                commit('resume', draft);
            } else {
                return Promise.reject({ message: window.i18n.t('Error loading draft') });
            }
        },

        async fromChecklist({ commit, rootGetters, dispatch }, checklistId) {
            let checklist = await checklistDB.get(checklistId);

            if (!checklist) {
                return Promise.reject({ message: window.i18n.t('Checklist not found!') });
            }

            if (checklist.form.params.format_version !== config.jsonFormatVersion) {
                await checklistDB.update(checklist.id);
                checklist = await checklistDB.get(checklistId);
            }

            if (checklist.form.params.format_version !== config.jsonFormatVersion) {
                return Promise.reject({
                    message: window.i18n.t('Incompatible form version: {formVersion} instead of {systemVersion}', {
                        formVersion: checklist.form.params.format_version,
                        systemVersion: config.jsonFormatVersion,
                    }),
                });
            }

            commit('start', {
                inspection_id: null,
                user_id: rootGetters['authentication/user'].id,
                checklist: checklist.id,
                todo: null,
                checklist_title: checklist.title,
                checklist_description: checklist.description,
                status: 0,
                site: null,
                sites: checklist.sites,
                users: checklist.users,
                started_at: null,
                conducted_at: null,
                form: checklist.form,
            });

            const draftId = await dispatch('saveDraft');
            await dispatch('fromDraft', draftId);
        },

        async fromInspection({ commit, rootGetters }, inspectionId) {
            const inspection = await inspectionService.get(inspectionId);

            if (!inspection) {
                return Promise.reject({ message: window.i18n.t('Checklist not found!') });
            }

            if (!inspection) return Promise.reject(new Error('User is offline'));

            commit('start', {
                inspection_id: inspection.inspection_id,
                user_id: rootGetters['authentication/user'].id,
                checklist: inspection.checklist_id,
                todo: inspection.todo_id,
                checklist_title: inspection.checklist_title,
                checklist_description: inspection.checklist_description,
                status: inspection.status,
                site: inspection.site,
                sites: inspection.checklist_sites,
                users: inspection.checklist_users,
                started_at: inspection.started_at,
                conducted_at: inspection.conducted_at,
                form: inspection.form,
            });

            return Promise.resolve(inspection);
        },

        async save({ commit, state, dispatch }) {
            // store end if there is no end yet
            if (!state.inspection.conducted_at) {
                commit('end');
            }


            // if the inspection is a resumed draft
            if (state.inspection.draft) {
                // if inspection is from a todo, remove draft reference from todo
                if (state.inspection.todo) {
                    await dispatch('todos/detachDraft', state.inspection.todo, { root: true });
                }

                // delete the draft and the reference to the draft
                await draftDB.delete(state.inspection.draft);
                delete state.inspection.draft;

                // delete draft id, if any
                delete state.inspection.id;
            }

            await inspectionQueueDB.put(state.inspection);
        },

        async saveDraft({ state }) {
            const draftId = await draftDB.put(state.inspection);
            if (state.inspection && state.inspection.todo) {
                // save draft to todo
                const todo = await todoDB.get(state.inspection.todo);
                // check if there is a todo for this inspection
                if (todo) {
                    // if the current draft for the todo is mot the same as the one we have
                    // this includes the case where todo.draft is undefined
                    if (todo.draft !== draftId) {
                        // if todo already has a draft, we delete that and set the new draft
                        // because there can only be one draft for a todo
                        // this should not happen
                        if (todo.draft) {
                            console.log('draft id mismatch');
                            await draftDB.delete(todo.draft);
                        }

                        // set draft reference and save todo
                        todo.draft = draftId;
                        await todoDB.put(todo);
                    }
                }
            }

            return Promise.resolve(draftId);
        },

        async cancel({ commit, state, dispatch }) {
            // if the inspection is a resumed draft
            if (state.inspection.draft) {
                // if inspection is from a todo, remove draft reference from todo
                if (state.inspection.todo) {
                    await dispatch('todos/detachDraft', state.inspection.todo, { root: true });
                }

                // delete the draft and the reference to the draft
                await draftDB.delete(state.inspection.draft);
                delete state.inspection.draft;
            }
        },

        issueSaved({ commit }, payload) {
            commit('issueSaved', payload);
        },

        issueRemoved({ commit }, parentId) {
            commit('issueRemoved', parentId);
        },

        siteChanged({ commit }, siteId) {
            commit('siteChanged', siteId);
        },

        issueItemModalOpened({ commit }, payload) {
            commit('issueItemModalOpened', payload);
        },

        issueItemModalClosed({ commit }) {
            commit('issueItemModalClosed');
        },

        outlineModalOpened({ commit }) {
            commit('outlineModalOpened');
        },

        outlineModalClosed({ commit }) {
            commit('outlineModalClosed');
        },

        outlineItemClicked({ commit }, pageId) {
            commit('outlineItemClicked', pageId);
        },
    },

    mutations: {
        enableAutoSave(state) {
            state.autoSaveEnabled = true;
        },
        disableAutoSave(state) {
            state.autoSaveEnabled = false;
        },
        inspectionValidationFailed(state, errors) {
            Vue.set(state, 'errors', errors);
            Vue.set(state, 'showErrors', true);
        },
        siteChanged(state, siteId) {
            let normalizedSiteId = null;
            if (siteId > 0) {
                normalizedSiteId = siteId;
            }
            Vue.set(state.inspection, 'site', normalizedSiteId);
            if (!state.inspection.issues) return;

            Object.keys(state.inspection.issues).forEach(
                (questionId) => Vue.set(state.inspection.issues[questionId].params, 'site', normalizedSiteId),
            );
        },

        issueSaved(state, { questionId, form }) {
            const issues = state.inspection.issues || {};
            Vue.set(state.inspection, 'issues', {
                ...issues,
                [questionId]: encoders.actionIssue({ ...form, site: state.inspection.site }),
            });
        },

        issueRemoved(state, questionId) {
            Vue.set(state.inspection, 'issues', R.omit([questionId], state.inspection.issues));
        },

        showNotesModal(state, item) {
            state.modals.notes.item = item;
            state.modals.notes.visible = true;
            state.autoSaveEnabled = false;
        },

        closeNotesModal(state) {
            state.modals.notes.item = null;
            state.modals.notes.visible = false;
            state.autoSaveEnabled = true;
        },

        showCancelModal(state) {
            state.modals.cancel.visible = true;
            state.autoSaveEnabled = false;
        },

        closeCancelModal(state) {
            state.modals.cancel.visible = false;
            state.autoSaveEnabled = true;
        },

        saveNotes(state, notes) {
            notes = notes.trim();
            if (notes.length > 0) {
                Vue.set(state.modals.notes.item, 'notes', notes);
            } else {
                Vue.delete(state.modals.notes.item, 'notes');
            }

            state.modals.notes.item = null;
            state.modals.notes.visible = false;
            state.autoSaveEnabled = true;
        },

        showPhotosModal(state, item) {
            state.modals.photos.item = item;
            state.modals.photos.visible = true;
            state.autoSaveEnabled = false;
        },

        closePhotosModal(state) {
            state.modals.photos.item = null;
            state.modals.photos.visible = false;
            state.autoSaveEnabled = true;
        },

        savePhotos(state, photos) {
            if (photos.length > 0) {
                Vue.set(state.modals.photos.item, 'photos', photos);
            } else {
                Vue.delete(state.modals.photos.item, 'photos');
            }

            Vue.set(state.modals.photos, 'item', null);
            Vue.set(state.modals.photos, 'visible', false);
            state.autoSaveEnabled = true;
        },

        reset(state) {
            Object.assign(state, getDefaultState());
        },

        start(state, inspection) {
            if (inspection.form.params.format_version !== config.jsonFormatVersion) {
                eventBus.$emit('error', window.i18n.t('Incompatible form version: {formVersion} instead of {systemVersion}', {
                    formVersion: inspection.form.params.format_version,
                    systemVersion: config.jsonFormatVersion,
                }));
                return;
            }
            state.inspection = inspection;

            if (!state.inspection.started_at) {
                state.inspection.started_at = now();
            }
        },

        resume(state, inspection) {
            if (inspection.form.params.format_version !== config.jsonFormatVersion) {
                eventBus.$emit('error', window.i18n.t('Incompatible form version: {formVersion} instead of {systemVersion}', {
                    formVersion: inspection.form.params.format_version,
                    systemVersion: config.jsonFormatVersion,
                }));
                return;
            }
            state.inspection = inspection;
        },

        end(state) {
            state.inspection.conducted_at = now();
        },

        nextPage(state) {
            if (state.inspection.form.items && (state.inspection.form.items.length - 1) > state.currentPage) {
                state.currentPage++;
            }
        },

        prevPage(state) {
            if (state.currentPage > 0) {
                state.currentPage--;
            }
        },

        addRepeatableSection(state, payload) {
            const parent = findItem(state.inspection.form, findParent(state.inspection.form, payload.item));
            const { items } = parent;
            const item = findItem(state.inspection.form, payload.item);
            let insert_at = 0;

            for (const i in items) {
                if (items[i].uuid === payload.item) {
                    insert_at = parseInt(i);
                    break;
                }
            }

            const section = cloneSection(item);

            if (typeof (item.repeated_sections) === 'undefined') {
                item.repeated_sections = [];
            }
            item.repeated_sections.push(section.uuid);

            section.title = `${section.title} ${item.repeated_sections.length}`;
            section.required = false;

            section.repeated = true;
            section.repeat = false;
            section.repeats = item.uuid;

            items.splice(insert_at, 0, section);
        },

        deleteRepeatedSection(state, payload) {
            const item = findItem(state.inspection.form, payload.item);
            const repeatingSection = findItem(state.inspection.form, item.repeats);

            deleteItem(state.inspection.form, payload.item);

            if (typeof (repeatingSection.repeated_sections) !== 'undefined') {
                const index = repeatingSection.repeated_sections.indexOf(item.uuid);
                if (index !== -1) repeatingSection.repeated_sections.splice(index, 1);

                for (const i in repeatingSection.repeated_sections) {
                    const repeatedSection = findItem(state.inspection.form, repeatingSection.repeated_sections[i]);
                    repeatedSection.title = `${repeatingSection.title} ${parseInt(i) + 1}`;
                }
            }
        },

        changeResponse(state, payload) {
            const item = findItem(state.inspection.form, payload.item);
            Vue.set(item, 'response', payload.response);
            Vue.set(item, 'responded', payload.responded);

            const errors = validateInspectionItem(item);
            if (errors.length <= 0) Vue.delete(state.errors, item.uuid);
            else Vue.set(state.errors, item.uuid, errors);

            if (item.check_conditions_for.length > 0) {
                for (const i in item.check_conditions_for) {
                    const conditionalSection = findItem(state.inspection.form, item.check_conditions_for[i]);
                    let allConditionsMet = true;
                    for (const j in conditionalSection.conditions) {
                        allConditionsMet = allConditionsMet
                            && conditionIsMet(conditionalSection.conditions[j], item);
                    }
                    conditionalSection.allConditionsMet = allConditionsMet;

                    // TODO: remove all responses from subitems if all conditions are not met
                }
            }
        },

        issueItemModalOpened(state, item) {
            Vue.set(state.modals, 'issueItem', {
                visible: true,
                item,
            });
        },

        issueItemModalClosed(state) {
            Vue.set(state.modals, 'issueItem', {
                visible: false,
                item: null,
            });
        },

        outlineModalOpened(state) {
            Vue.set(state.modals, 'outline', {
                visible: true,
            });
        },

        outlineModalClosed(state) {
            Vue.set(state.modals, 'outline', {
                visible: false,
            });
        },

        outlineItemClicked(state, pageId) {
            Vue.set(state.modals.outline, 'visible', false);
            const pageIndex = state.inspection.form.items.findIndex((page) => page.uuid === pageId);
            Vue.set(state, 'currentPage', pageIndex);
        },
    },

};

function getDefaultState() {
    return {
        inspection: null,
        currentPage: 0,
        errors: {},
        showErrors: false,
        modals: {
            notes: { visible: false, item: null },
            photos: { visible: false, item: null },
            cancel: { visible: false },
            issueItem: { visible: false, item: null },
            outline: { visible: false },
        },
        autoSaveEnabled: true,
    };
}

function cloneSection(originalSection) {
    let stringifiedSection = JSON.stringify(originalSection);

    stringifiedSection = reassignUUIDs(originalSection, stringifiedSection);

    const section = JSON.parse(stringifiedSection);

    return section;
}

function reassignUUIDs(item, text) {
    if (item.uuid) {
        const new_uuid = uuidv4();
        text = text.replace(new RegExp(item.uuid, 'g'), new_uuid);
    }

    if (item.items && item.items.length) {
        for (const i in item.items) {
            text = reassignUUIDs(item.items[i], text);
        }
    }

    return text;
}

function conditionIsMet(condition, item) {
    switch (item.response_type) {
        case 'list': return listConditionIsMet(condition, item.responded, item.response);
        case 'number': return numberConditionIsMet(condition, item.responded, item.response);
        case 'checkbox': return checkboxConditionIsMet(condition, item.responded, item.response);
    }
}

function listConditionIsMet(condition, responded, response) {
    let conditionArray;
    let responseArray;

    if (condition.value && condition.value.constructor === Array) {
        conditionArray = condition.value;
        conditionArray.sort();
    } else {
        conditionArray = [condition.value];
    }
    responseArray = response;
    responseArray.sort();

    switch (condition.operator) {
        case 'is': return _.xor(conditionArray, responseArray).length === 0;
        case 'is_not': return _.xor(conditionArray, responseArray).length > 0;
        case 'empty': return responseArray.length === 0;
        case 'not_empty': return responseArray.length > 0;
        case 'one_of': return responseArray.length > 0 && _.difference(responseArray, conditionArray).length === 0;
        case 'not_one_of': return responseArray.length > 0 && _.intersection(responseArray, conditionArray).length === 0;
        case 'includes': return responseArray.length > 0 && _.intersection(responseArray, conditionArray).length === conditionArray.length;
    }
}

function numberConditionIsMet(condition, responded, response) {
    if (!responded) {
        return false;
    }

    // unfortuanetly Number("") is 0 for some reason
    // we have to check for that
    if (response.trim() === '') {
        return false;
    }

    const parsedResponse = Number(response);

    if (isNaN(parsedResponse)) {
        return false;
    }

    switch (condition.operator) {
        case '<': return parsedResponse < condition.value;
        case '<=': return parsedResponse <= condition.value;
        case '=': return parsedResponse == condition.value;
        case '!=': return parsedResponse != condition.value;
        case '>=': return parsedResponse >= condition.value;
        case '>': return parsedResponse > condition.value;
    }
}

function checkboxConditionIsMet(condition, responded, response) {
    switch (condition.operator) {
        case 'is_true': return response === true;
        case 'is_false': return response !== true;
    }
}

function findItem(item, uuid) {
    if (item.uuid === uuid) {
        return item;
    } if (item.items) {
        for (const i in item.items) {
            const res = findItem(item.items[i], uuid);
            if (res !== false) {
                return res;
            }
        }
    }
    return false;
}

function deleteItem(item, uuid) {
    if (item.check_conditions_for && item.check_conditions_for.length) {
        for (var i in item.check_conditions_for) {
            if (item.check_conditions_for[i] === uuid) {
                item.check_conditions_for.splice(i, 1);
            }
        }
    }

    if (item.items && item.items.length) {
        for (var i in item.items) {
            if (item.items[i].uuid === uuid) {
                item.items.splice(i, 1);
            } else {
                deleteItem(item.items[i], uuid);
            }
        }
    }
}

function findParent(item, uuid) {
    if (!item.items) {
        return false;
    }

    for (const i in item.items) {
        if (item.items[i].uuid === uuid) {
            return item.uuid;
        }
        const res = findParent(item.items[i], uuid);
        if (res !== false) {
            return res;
        }
    }

    return false;
}

function uuidv4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));
}
